import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe, Languages } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const CountrySelector = () => {
  const {
    selectedCountry,
    countries,
    changeCountry,
    selectedLanguage,
    languages,
    changeLanguage
  } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowLanguages(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const countryList = Object.values(countries);
  const languageList = Object.values(languages);

  return (
    <div className="relative flex items-center gap-2" ref={dropdownRef}>
      {/* Country Selector */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setShowLanguages(false);
        }}
        className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors text-sm font-medium text-slate-700"
        data-testid="country-selector-btn"
      >
        <span className="text-lg">{selectedCountry.flag}</span>
        <span className="hidden sm:inline">{selectedCountry.code}</span>
        <span className="text-slate-500">|</span>
        <span className="font-semibold">{selectedCountry.symbol}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Language Selector */}
      <button
        onClick={() => {
          setShowLanguages(!showLanguages);
          setIsOpen(false);
        }}
        className="flex items-center gap-1.5 px-2.5 py-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors text-sm font-medium text-slate-700"
        data-testid="language-selector-btn"
        title="Change Language"
      >
        <Languages className="h-4 w-4" />
        <span className="hidden sm:inline text-xs uppercase">{selectedLanguage.code}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${showLanguages ? 'rotate-180' : ''}`} />
      </button>

      {/* Country Dropdown */}
      {isOpen && (
        <div className="absolute right-16 sm:right-20 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
              <Globe className="h-3 w-3" /> Select Region
            </p>
          </div>
          {countryList.map((country) => (
            <button
              key={country.code}
              onClick={() => {
                changeCountry(country.code);
                setIsOpen(false);

                const pathCode = country.code.toLowerCase();
                const currentSegments = window.location.pathname.split('/').filter(Boolean);
                const knownCodes = ['in', 'my', 'id', 'th'];

                if (currentSegments.length > 0 && knownCodes.includes(currentSegments[0])) {
                  currentSegments[0] = pathCode;
                } else {
                  currentSegments.unshift(pathCode);
                }

                const newPath = '/' + currentSegments.join('/');

                if (window.location.pathname !== newPath) {
                  window.location.href = newPath;
                }
              }}
              data-testid={`country-option-${country.code}`}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${selectedCountry.code === country.code ? 'bg-blue-50' : ''
                }`}
            >
              <span className="text-xl">{country.flag}</span>
              <div className="flex-1 text-left">
                <p className={`font-medium ${selectedCountry.code === country.code ? 'text-blue-600' : 'text-slate-900'}`}>
                  {country.name}
                </p>
                <p className="text-xs text-slate-500">{country.currency} ({country.symbol})</p>
              </div>
              {selectedCountry.code === country.code && (
                <div className="h-2 w-2 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Language Dropdown */}
      {showLanguages && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
              <Languages className="h-3 w-3" /> Select Language
            </p>
          </div>
          {languageList.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setShowLanguages(false);
              }}
              data-testid={`language-option-${lang.code}`}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${selectedLanguage.code === lang.code ? 'bg-blue-50' : ''
                }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div className="flex-1 text-left">
                <p className={`font-medium ${selectedLanguage.code === lang.code ? 'text-blue-600' : 'text-slate-900'}`}>
                  {lang.nativeName}
                </p>
                <p className="text-xs text-slate-500">{lang.name}</p>
              </div>
              {selectedLanguage.code === lang.code && (
                <div className="h-2 w-2 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
